import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Container } from 'react-bootstrap';
import { FaSave, FaPlus, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import { readStoredAdminUser } from './adminAccessControl';
import { OUTLET_TYPE_OPTIONS } from './outletTypeOptions';
import {
    RESTAURANT_MENU_TEMPLATE_OPTIONS,
    supportsRestaurantMenuTheme,
} from './restaurantMenuTemplateOptions';

function AddOutlet() {
    const navigate = useNavigate();
    const currentUser = readStoredAdminUser();
    const defaultParentOutlet =
        currentUser.actor_type === 'outlet' ? Number(currentUser.outlet_id || 0) : 0;
    const [outletData, setOutletData] = useState({
        outlet_name: '',
        outlet_address: '',
        outlet_phone: '',
        outlet_email: '',
        outlet_sosmed: '',
        outlet_type: '',
        menu_template: 'classic_warm',
        menu_background: '',
        restaurant_table_count: 12,
        restaurant_tax_percent: 0,
        restaurant_service_percent: 0,
        username: '',
        password: '',
        status: 0,
        parent: defaultParentOutlet,
    });

    const [outletList, setOutletList] = useState([]);

    const normalizePercentInput = (value) => Math.min(100, Math.max(0, Number.parseFloat(value) || 0));

    useEffect(() => {
        const fetchOutletList = async () => {
            const token = localStorage.getItem('sessionToken');
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outletlist`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ limit: 100, start: 0 }),
                });

                const data = await response.json();
                if (data.error === "0") {
                    setOutletList(data.results);  // Assuming 'results' contains the outlet list
                } else {
                    Swal.fire('Error!', data.message || 'Failed to fetch outlet list.', 'error');
                }
            } catch (err) {
                console.error(err);
                Swal.fire('Error!', 'Failed to fetch outlet list.', 'error');
            }
        };

        fetchOutletList();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setOutletData(prevData => ({
            ...prevData,
            [name]: name === 'status' || name === 'parent' || name === 'restaurant_table_count'
                ? parseInt(value, 10) || 0
                : name === 'restaurant_tax_percent' || name === 'restaurant_service_percent'
                    ? normalizePercentInput(value)
                    : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('sessionToken');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}addoutlet`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(outletData),
            });

            const data = await response.json();
            if (data.error === "0") {
                Swal.fire('Success!', 'Outlet added successfully.', 'success');
                navigate('/outlets');
            } else {
                Swal.fire('Error!', data.message || 'Failed to add outlet.', 'error');
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error!', 'Failed to add outlet.', 'error');
        }
    };

    const handleCancel = () => {
        navigate('/outlets');
    };

    return (
        <div>
            <Navigation />
            <Container fluid className="admin-page-shell mt-4 px-4">
                <AdminPageHeader
                    icon={FaPlus}
                    eyebrow="Branch administration"
                    title="Create Outlet"
                    description="Tambahkan outlet baru dengan data operasional, kontak, dan akun akses yang lengkap."
                    breadcrumbs={[
                        { label: 'Dashboard', to: '/dashboardadmin' },
                        { label: 'Outlet Management', to: '/outlets' },
                        { label: 'Create Outlet' },
                    ]}
                />

                <div className="card">
                    <div className="card-body">
                        <Form onSubmit={handleSubmit}>
                            <h5 className="mb-4"><FaPlus /> Add Outlet</h5>
                            <Form.Group controlId="formOutletName">
                                <Form.Label>Outlet Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="outlet_name"
                                    value={outletData.outlet_name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter outlet name"
                                />
                            </Form.Group>
                            <Form.Group controlId="formOutletAddress">
                                <Form.Label>Outlet Address</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="outlet_address"
                                    value={outletData.outlet_address}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter outlet address"
                                />
                            </Form.Group>
                            <Form.Group controlId="formOutletPhone">
                                <Form.Label>Outlet Phone</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="outlet_phone"
                                    value={outletData.outlet_phone}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter outlet phone"
                                />
                            </Form.Group>
                            <Form.Group controlId="formOutletEmail">
                                <Form.Label>Outlet Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="outlet_email"
                                    value={outletData.outlet_email}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter outlet email"
                                />
                            </Form.Group>
                            <Form.Group controlId="formOutletSosmed">
                                <Form.Label>Outlet Social Media</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="outlet_sosmed"
                                    value={outletData.outlet_sosmed}
                                    onChange={handleChange}
                                    placeholder="Enter outlet social media link"
                                />
                            </Form.Group>
                            <Form.Group controlId="formOutletType">
                                <Form.Label>Outlet Type</Form.Label>
                                <Form.Select
                                    name="outlet_type"
                                    value={outletData.outlet_type}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Outlet Type</option>
                                    {OUTLET_TYPE_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text>
                                    Gunakan tipe outlet seperti Gym Center, Gym + Store, Restaurant, POS Store, Store Bahan Bangunan, Hybrid, Cafe + Gym, atau Cafe + Store agar workspace kasir mengikuti jenis bisnis outlet.
                                </Form.Text>
                            </Form.Group>
                            {supportsRestaurantMenuTheme(outletData.outlet_type) ? (
                                <>
                                    <hr className="my-4" />
                                    <h6 className="mb-3">Restaurant Menu Theme</h6>
                                    <Form.Group controlId="formMenuTemplate" className="mb-3">
                                        <Form.Label>Menu Template</Form.Label>
                                        <Form.Select
                                            name="menu_template"
                                            value={outletData.menu_template}
                                            onChange={handleChange}
                                        >
                                            {RESTAURANT_MENU_TEMPLATE_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text>
                                            Template ini dipakai di halaman menu publik restoran agar tiap outlet punya nuansa sendiri.
                                        </Form.Text>
                                    </Form.Group>
                                    <Form.Group controlId="formMenuBackground" className="mb-3">
                                        <Form.Label>Background Image URL</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="menu_background"
                                            value={outletData.menu_background}
                                            onChange={handleChange}
                                            placeholder="https://..."
                                        />
                                        <Form.Text>
                                            Opsional. Isi URL gambar background jika outlet ingin tampilan menu publik yang berbeda dari template default.
                                        </Form.Text>
                                    </Form.Group>
                                    <Form.Group controlId="formRestaurantTableCount" className="mb-3">
                                        <Form.Label>Jumlah Meja Restoran</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            name="restaurant_table_count"
                                            value={outletData.restaurant_table_count}
                                            onChange={handleChange}
                                        />
                                        <Form.Text>
                                            Dipakai oleh dashboard kasir untuk menampilkan board meja yang bisa langsung diklik saat menerima order dan pembayaran.
                                        </Form.Text>
                                    </Form.Group>
                                    <Form.Group controlId="formRestaurantTaxPercent" className="mb-3">
                                        <Form.Label>Tax Persentase (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            name="restaurant_tax_percent"
                                            value={outletData.restaurant_tax_percent}
                                            onChange={handleChange}
                                        />
                                        <Form.Text>
                                            Persentase pajak default untuk transaksi POS restaurant di outlet ini. Nilai ini baru akan dipakai di flow kasir pada tahap integrasi berikutnya.
                                        </Form.Text>
                                    </Form.Group>
                                    <Form.Group controlId="formRestaurantServicePercent" className="mb-3">
                                        <Form.Label>Service Persentase (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            name="restaurant_service_percent"
                                            value={outletData.restaurant_service_percent}
                                            onChange={handleChange}
                                        />
                                        <Form.Text>
                                            Persentase service charge default untuk outlet restaurant ini agar tidak perlu diinput manual per transaksi.
                                        </Form.Text>
                                    </Form.Group>
                                </>
                            ) : null}
                            <Form.Group controlId="formUsername">
                                <Form.Label>Username</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="username"
                                    value={outletData.username}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter username"
                                />
                            </Form.Group>
                            <Form.Group controlId="formPassword">
                                <Form.Label>Password</Form.Label>
                                <Form.Control
                                    type="password"
                                    name="password"
                                    value={outletData.password}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter password"
                                />
                            </Form.Group>
                            <Form.Group controlId="formStatus">
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    name="status"
                                    value={outletData.status}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Status</option>
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group controlId="formParent">
                                <Form.Label>Parent Outlet</Form.Label>
                                <Form.Select
                                    name="parent"
                                    value={outletData.parent}
                                    onChange={handleChange}
                                >
                                    {currentUser.actor_type !== 'outlet' ? (
                                        <option value="0">No Parent Outlet</option>
                                    ) : null}
                                    {outletList.map(outlet => (
                                        <option key={outlet.id ?? outlet.ID} value={outlet.id ?? outlet.ID}>
                                            {outlet.outlet_name ?? outlet.OutletName}
                                        </option>
                                    ))}
                                </Form.Select>
                                {currentUser.actor_type === 'outlet' ? (
                                    <Form.Text>
                                        Outlet login hanya dapat membuat sub-outlet di bawah outlet yang berada dalam scope cabangnya.
                                    </Form.Text>
                                ) : null}
                            </Form.Group>
                            <div className="d-flex gap-2 mt-3">
                                <Button variant="primary" type="submit">
                                    <FaSave /> Save Outlet
                                </Button>
                                <Button variant="secondary" onClick={handleCancel}>
                                    <FaTimes /> Cancel
                                </Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Container>
        </div>
    );
}

export default AddOutlet;
